import React, { useMemo, useState } from 'react';
import { Badge } from 'antd';
import {
  AppstoreOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RootState } from '../../../types/store';
import {
  setCartDrawerOpen,
  setSelectedLocation,
  selectCartTotalElements,
  selectCustomerId,
  selectConfig,
  selectSelectedLocation,
} from '../slice/storefrontSlice';
import { LocationStatusModal } from './LocationStatusModal';
import type { IStoreLocation } from '../types/storefront.types';
import { useGetMyOrdersQuery } from '../api/storefrontApi';
import { isOrderActive } from '../views/order/utils/order.tracking.util';

interface DockButtonProps {
  label: string;
  text: string;
  icon: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
}

const DockButton: React.FC<DockButtonProps> = ({
  label,
  text,
  icon,
  isActive = false,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-current={isActive ? 'page' : undefined}
    className="dock-btn"
  >
    {icon}
    <span className="dock-label">{text}</span>
  </button>
);

export const StorefrontFooterDock: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const config = useSelector(selectConfig);
  const selectedLocation = useSelector(selectSelectedLocation);
  const customerId = useSelector(selectCustomerId);
  const subdomain = useSelector((state: RootState) => state.storefront.subdomain);
  const isCartOpen = useSelector((state: RootState) => state.storefront.isCartDrawerOpen);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  const cartItemCount = useSelector(selectCartTotalElements);
  const storeHome = subdomain ? `/store/${subdomain}` : null;
  const isMenuPage =
    location.pathname === storeHome ||
    location.pathname === `${storeHome}/` ||
    location.pathname.startsWith(`${storeHome}/menu`);
  const isOrdersPage =
    location.pathname.includes('/orders') || location.pathname.includes('/order/');
  const isCheckoutPage = location.pathname.includes('/checkout');

  // Sincronización ligera de órdenes para mantener el Badge en vivo
  const { data: customerOrders } = useGetMyOrdersQuery(
    {
      customerId: customerId || '',
      partnerId: config?.partner?.id || '',
      partnerLocationId: selectedLocation?.id || '',
    },
    {
      skip: !customerId || !config?.partner?.id || !selectedLocation?.id,
      pollingInterval: 12000,
    }
  );

  const activeOrdersCount = useMemo(() => {
    if (!customerOrders || !Array.isArray(customerOrders)) return 0;
    return customerOrders.filter((o: any) => isOrderActive(o.status)).length;
  }, [customerOrders]);

  const handleLocationSelect = (loc: IStoreLocation) => {
    dispatch(setSelectedLocation(loc));
    setIsLocationModalOpen(false);
  };

  if (isCartOpen || isCheckoutPage) return null;

  return (
    <>
      <nav aria-label="Navegacion rapida" className="storefront-dock">
        {/* 1. Menú / Carta principal */}
        <DockButton
          label="Carta de productos"
          text="Menú"
          icon={<AppstoreOutlined style={{ fontSize: 20 }} />}
          isActive={isMenuPage}
          onClick={() => storeHome && navigate(storeHome)}
        />

        {/* 2. Sedes / Sucursales */}
        <DockButton
          label="Sedes de la tienda"
          text="Sedes"
          icon={<EnvironmentOutlined style={{ fontSize: 20 }} />}
          onClick={() => setIsLocationModalOpen(true)}
        />

        {/* 3. Pedidos con Badge dinámico si hay orden activa */}
        <DockButton
          label={`Mis Pedidos${activeOrdersCount > 0 ? ` (${activeOrdersCount} en curso)` : ''}`}
          text="Pedidos"
          icon={
            <Badge
              count={activeOrdersCount}
              color="var(--color-accent, #EA580C)"
              offset={[4, -2]}
              style={{
                fontFamily: 'var(--font-mono)',
                fontWeight: 700,
                fontSize: 10,
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 20 }} />
            </Badge>
          }
          isActive={isOrdersPage}
          onClick={() => storeHome && navigate(`${storeHome}/orders`)}
        />

        {/* 4. Carrito de compras */}
        <DockButton
          label={`Ver carrito${cartItemCount > 0 ? ` (${cartItemCount})` : ''}`}
          text="Carrito"
          icon={
            <Badge
              count={cartItemCount}
              color="var(--color-badge)"
              offset={[4, -2]}
              style={{
                fontFamily: 'var(--font-mono)',
                fontWeight: 700,
                fontSize: 11,
                color: 'var(--color-badge-contrast)',
              }}
            >
              <ShoppingOutlined style={{ fontSize: 20, color: 'currentColor' }} />
            </Badge>
          }
          onClick={() => dispatch(setCartDrawerOpen(true))}
        />
      </nav>

      <LocationStatusModal
        open={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        locations={config?.partner?.locations || []}
        selectedLocationId={selectedLocation?.id}
        onSelect={handleLocationSelect}
      />
    </>
  );
};
